<script lang="ts">

  import httpClient from "$lib/axios-client"
  import { ErrorActionPage, PermissionPage } from "$lib/components/page"
  import LoadingVoidPage from "$lib/components/page/loading-void-page.svelte"
  import { PERMISSIONS } from "$lib/data/const/permissions"
  import { type Device, DeviceEmpty } from "$lib/data/type/device/device"
  import { DeviceTokenEmpty } from "$lib/data/type/device/device-token"
  import { deviceStore } from "$lib/stores/device-store.svelte"
  import { createQuery } from "@tanstack/svelte-query"
  import Cookies from "js-cookie"
  import { ulid } from "ulid"

  const { children } = $props()

  const permissions = $derived(deviceStore.permissions)

  const device = createQuery(() => ({
    queryKey: ["_device"],
    queryFn: async () => {

      const deviceID = Cookies.get("x-device-id") || ulid()
      const userAgent = navigator.userAgent

      let platform = "Unknown"
      if (/android/i.test(userAgent)) platform = "Android"
      if (/iPad|iPhone|iPod/.test(userAgent)) platform = "iOS"
      if (/Win/.test(userAgent)) platform = "Windows"

      let data: Device
      let dataToken = DeviceTokenEmpty

      if (
        import.meta.env.PROD &&
        typeof _device !== "undefined"
      ) {

        const information = JSON.parse(await _device.information())
        const informationToken = JSON.parse(await _device.token())
        data = { ...information, is_test: false, is_guest: false, is_phone: window.innerWidth <= 480 }
        dataToken = { ...informationToken }

      } else if (import.meta.env.PROD) {

        const regionId = Intl.DateTimeFormat().resolvedOptions().timeZone
        const regionOffset = new Date().getTimezoneOffset() * -60 * 1000

        data = {
          app_version: "Browser",
          is_test: false,
          is_guest: false,
          is_phone: window.innerWidth <= 480,
          device_id: deviceID,
          device_brand: "Browser",
          device_name: `Browser - ${platform}`,
          device_product: "x",
          device_os_code: "Browser",
          device_os: 0,
          region_id: regionId,
          region_offset: regionOffset,
        }

      } else if (import.meta.env.DEV) {

        const regionId = Intl.DateTimeFormat().resolvedOptions().timeZone
        const regionOffset = new Date().getTimezoneOffset() * -60 * 1000

        data = {
          app_version: "Browser",
          is_test: true,
          is_guest: false,
          is_phone: window.innerWidth <= 480,

          device_id: deviceID,
          device_brand: "Browser",
          device_name: "unknown",
          device_product: "x",
          device_os_code: "Browser",
          device_os: 0,

          region_id: regionId,
          region_offset: regionOffset,
        }

      } else {
        data = DeviceEmpty
      }

      Cookies.set("x-device-id", deviceID, { expires: 365 })

      httpClient.defaults.headers["device-id"] = data.device_id
      httpClient.defaults.headers["device-name"] = data.device_name
      httpClient.defaults.headers["device-region-id"] = data.region_id
      httpClient.defaults.headers["device-region-offset"] = data.region_offset

      const permissions = await Promise
        .all(
          PERMISSIONS
            .map(async (permission) => {

              const permissionStatus = await navigator.permissions?.query({ name: permission.name as PermissionName })

              const is_granted = typeof _device !== "undefined"
                ? true
                : permissionStatus?.state == "granted"

              return {
                ...permission,
                is_granted: is_granted
              }
            })
        )

      deviceStore.update(platform, data, dataToken, permissions)

      return true
    }
  }))


  $effect(() => {

    void deviceStore.geolocationRequestId

    if (
      permissions.isEmpty() ||
      !permissions.every((permission) => permission.is_granted)
    ) {
      return
    }

    const id = navigator.geolocation.watchPosition(
      (position) => {
        deviceStore.geolocationUpdate(position)
      },
      () => {
      },
      {
        enableHighAccuracy: true,
        timeout: 2000,
        maximumAge: 0,
      }
    )

    return () => {
      navigator.geolocation.clearWatch(id)
    }
  })

</script>

{#if device.isFetching}
  <LoadingVoidPage/>
{:else if device.isError}
  <ErrorActionPage
    message="Halaman gagal dimuat. Silakan coba lagi"
    useActionReload={true}
  />
{:else if !permissions.every((permission) => permission.is_granted)}
  <PermissionPage/>
{:else}
  {@render children()}
{/if}
